// Live pages (ADR-0032; owner request 2026-09-30): Client docs, Folders and Activity follow the engine as it publishes. One small
// signal per engagement — `sync_state` (cloud 0009), a row per published table that moves each time the engine publishes it — tells
// the page when to reload and gives its "updated <time>". Realtime delivers the signal at once; a short poll of the same few rows
// backs it up (a dropped socket, a sleeping laptop). Where the signal cannot be read (0009 not applied yet) the page reloads its
// tables on a slower poll instead and says so. Nothing here computes what the engine published; it only decides when to read it.
//
// NOTE for the merge: the Action center / chat work has its own live-refresh on the `workspace` topic; this helper is separate
// (engagement tables by publish signal) and the two can be unified around `useLiveTables`.
import { useEffect, useRef, useState } from "react";
import type { DataSource } from "./source";

export type LiveMode = "live" | "polling" | "loading";
export interface LiveState {
  /** When the engine last published any of the page's tables (ISO), or — while that is unknown — when the page last loaded them. */
  updatedAt: string | null;
  /** When the page last checked. */
  checkedAt: string | null;
  /** live: Realtime + a check every `pollMs`; polling: no publish signal readable here, the tables are reloaded every `fallbackMs`. */
  mode: LiveMode;
}

export const POLL_MS = 15_000;
export const FALLBACK_MS = 30_000;

/** Reload the page (`onChange`) whenever the engine publishes one of `tables` for the engagement. */
export function useLiveTables(ds: DataSource, engagementId: string, tables: string[], onChange: () => void,
  opts: { pollMs?: number; fallbackMs?: number; debounceMs?: number } = {}): LiveState {
  const { pollMs = POLL_MS, fallbackMs = FALLBACK_MS, debounceMs = 500 } = opts;
  const [state, setState] = useState<LiveState>({ updatedAt: null, checkedAt: null, mode: "loading" });
  const cb = useRef(onChange);
  cb.current = onChange;
  const key = tables.join(",");
  useEffect(() => {
    let alive = true;
    let last: string | null = null;                     // the published signature last seen ("?" while unknown)
    let reloadedAt = Date.now();
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const names = key.split(",");
    const check = async () => {
      let map: Record<string, string> | null = null;
      try { map = ds.publishedAt ? await ds.publishedAt(engagementId, names) : null; } catch { map = null; }
      if (!alive) return;
      const now = new Date().toISOString();
      if (map === null) {
        if (last !== null && Date.now() - reloadedAt >= fallbackMs) { reloadedAt = Date.now(); cb.current(); }
        last = "?";
        setState({ updatedAt: new Date(reloadedAt).toISOString(), checkedAt: now, mode: "polling" });
        return;
      }
      const sig = names.map((t) => `${t}=${map![t] ?? ""}`).join("|");
      if (last !== null && sig !== last) { reloadedAt = Date.now(); cb.current(); }
      last = sig;
      const newest = Object.values(map).filter(Boolean).sort().pop() ?? null;
      setState({ updatedAt: newest, checkedAt: now, mode: "live" });
    };
    void check();
    const off = ds.watchTables?.(engagementId, names, () => { clearTimeout(debounce); debounce = setTimeout(() => void check(), debounceMs); });
    const timer = setInterval(() => { if (typeof document === "undefined" || document.visibilityState !== "hidden") void check(); }, Math.min(pollMs, fallbackMs));
    return () => { alive = false; off?.(); clearInterval(timer); clearTimeout(debounce); };
  }, [ds, engagementId, key, pollMs, fallbackMs, debounceMs]);
  return state;
}
